import { Link, useSearch } from '@tanstack/react-router'
import { useMemo } from 'react'
import { z } from 'zod'

import { Button } from '@/components/base/button'
import { Skeleton } from '@/components/base/skeleton'
import { useI18n } from '@/i18n/i18n-react'
import { ChevronLeft } from '@/icons/chevron-left'
import { ChevronRight } from '@/icons/chevron-right'
import { clx } from '@/utils/clx'

export const DEFAULT_PAGE_SIZE = 30

const zSearch = z.object({
	limit: z.number().min(1).max(100).optional().default(DEFAULT_PAGE_SIZE),
	offset: z.number().optional().default(0)
})

export const Pagination = ({
	totalCount,
	className,
	limit: limitOverride,
	offset: offsetOverride,
	onOffsetChange,
	compact = false
}: {
	totalCount?: number
	className?: string
	limit?: number
	offset?: number
	onOffsetChange?: (offset: number) => void
	/**
	 * Drops the numbered page buttons, leaving the result count and Previous/Next. For narrow
	 * columns: the full row needs ~550px, which overflows a list panel, and the `sm:flex-row` below
	 * is a viewport breakpoint so it cannot tell that the column itself is narrow.
	 */
	compact?: boolean
}) => {
	const { L } = useI18n()

	const search = useSearch({ strict: false })
	const parsed = zSearch.parse(search)
	const limit = limitOverride ?? parsed.limit
	const currentOffset = offsetOverride ?? parsed.offset ?? 0
	const currentPage = Math.floor(currentOffset / limit) + 1
	const controlled = onOffsetChange !== undefined

	const isLoading = totalCount === undefined
	totalCount = totalCount || 0
	const totalPages = Math.ceil(totalCount / limit)
	const hasNextPage = currentPage < totalPages
	const hasPrevPage = currentPage > 1

	const paginationInfo = useMemo(() => {
		const start = currentOffset + 1
		const end = Math.min(currentOffset + limit, totalCount)
		return { start, end, total: totalCount }
	}, [currentOffset, limit, totalCount])

	// Create an array of page numbers which is 5 items or less with the current page in the middle
	const pageNumbers = useMemo(() => {
		const startNumber = Math.max(1, Math.min(currentPage - 2, totalPages === 0 ? 1 : totalPages - 4))
		const endNumber = Math.min(startNumber + 4, totalPages === 0 ? startNumber + 4 : totalPages)
		return Array.from({ length: endNumber - startNumber + 1 }, (_, i) => startNumber + i)
	}, [totalPages, currentPage])

	const pageButton = (pageNumber: number) => {
		const button = (
			<Button
				type='button'
				variant={pageNumber === currentPage ? 'secondary' : 'transparent'}
				size='small'
				className='h-8 w-8 p-0'
				onClick={controlled ? () => onOffsetChange((pageNumber - 1) * limit) : undefined}
			>
				{pageNumber}
			</Button>
		)
		if (controlled) {
			return button
		}
		return (
			<Link
				key={pageNumber}
				to='.'
				search={prev => ({
					...prev,
					limit,
					offset: (pageNumber - 1) * limit
				})}
			>
				{button}
			</Link>
		)
	}

	const prevButton = (
		<Button
			type='button'
			variant='transparent'
			disabled={!hasPrevPage}
			className='flex items-center gap-1 p-2'
			onClick={controlled ? () => onOffsetChange((currentPage - 2) * limit) : undefined}
		>
			<ChevronLeft className='h-4 w-4' />
			{L.Pagination.Previous()}
		</Button>
	)

	const nextButton = (
		<Button
			type='button'
			variant='transparent'
			disabled={!hasNextPage}
			className='flex items-center gap-1 p-2'
			onClick={controlled ? () => onOffsetChange(currentPage * limit) : undefined}
		>
			{L.Pagination.Next()}
			<ChevronRight className='h-4 w-4' />
		</Button>
	)

	return (
		<div
			className={clx(
				'txt-compact-small-plus flex flex-col items-center justify-between gap-4 sm:flex-row',
				className
			)}
		>
			<div className={clx('text-muted-foreground text-sm', { 'whitespace-nowrap': compact })}>
				{paginationInfo.total === 0
					? L.Pagination.NoResults()
					: L.Pagination.Showing({
							start: paginationInfo.start,
							end: paginationInfo.end,
							total: paginationInfo.total
						})}
			</div>
			{!isLoading && paginationInfo.total === 0 ? null : (
				<div className={clx('flex items-center gap-2', { 'shrink-0': compact })}>
					{controlled ? (
						prevButton
					) : (
						<Link
							to='.'
							search={prev => ({
								...prev,
								limit,
								offset: (currentPage - 2) * limit
							})}
							disabled={!hasPrevPage}
						>
							{prevButton}
						</Link>
					)}

					{!compact && (
						<div className='flex items-center gap-1'>
							{isLoading ? (
								<Skeleton className='h-8 w-8' />
							) : (
								pageNumbers.map(pageNumber => <span key={pageNumber}>{pageButton(pageNumber)}</span>)
							)}
						</div>
					)}

					{controlled ? (
						nextButton
					) : (
						<Link
							disabled={!hasNextPage}
							to='.'
							search={prev => ({
								...prev,
								limit,
								offset: currentPage * limit
							})}
						>
							{nextButton}
						</Link>
					)}
				</div>
			)}
		</div>
	)
}
